import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Loader, AlertCircle, ChevronLeft, ChevronRight, Moon, Sun, Bookmark } from 'lucide-react';
import { MenuCategory, MenuItem } from '../services/ordersApi';
import { dineInMenuQueryOptions } from '../queries/menu';
import { DineInThemeShell } from '../components/DineInThemeShell';
import { HallCheckPanel } from '../components/HallCheckPanel';
import { MenuFavoriteButton } from '../components/MenuFavoriteButton';
import { MenuFavoritesOnboarding } from '../components/MenuFavoritesOnboarding';
import { DineInMenuThemeTokens, useDineInMenuTheme } from '../hooks/useDineInMenuTheme';
import { useMenuFavoritesStore } from '../store/menuFavoritesStore';
import { hallApi, type HallTable } from '../services/hallApi';
import { buildCaseHallTable, isHallCaseTable } from '../data/hallCaseMock';

type MenuSection = {
  id: string;
  name: string;
  up: boolean;
  categories: MenuCategory[];
};

const UP_TAG = 'up';

function tagName(tag: { name?: string } | undefined): string {
  return tag?.name?.trim() ?? '';
}

function isUpTagName(name: string): boolean {
  return name.toLowerCase() === UP_TAG;
}

function looksLikeBar(name: string): boolean {
  const n = name.toLowerCase();
  return /(барн|барная|коктейл|вино|напит|пиво|алкогол|безалкогол|кофе|чай|соки|вода|shot|ликер|ликёр)/.test(
    n,
  );
}

function isHighlightedItem(item: MenuItem): boolean {
  return (item.tags ?? []).some((t) => tagName(t).toLowerCase() === 'выделено');
}

function categoryGoesUp(cat: MenuCategory): boolean {
  if ((cat.tags ?? []).some((t) => isUpTagName(tagName(t)))) return true;
  const group = groupingTag(cat);
  if (group && looksLikeBar(tagName(group))) return true;
  return looksLikeBar(cat.name);
}

function groupingTag(cat: MenuCategory) {
  return (cat.tags ?? []).find((t) => {
    const name = tagName(t);
    if (!name) return false;
    const lower = name.toLowerCase();
    return lower !== UP_TAG && lower !== 'выделено';
  });
}

function itemGroupName(item: MenuItem): string | null {
  const tag = (item.tags ?? []).find((t) => {
    const name = tagName(t);
    if (!name) return false;
    const lower = name.toLowerCase();
    return lower !== UP_TAG && lower !== 'выделено';
  });
  return tag ? tagName(tag) : null;
}

type ItemGroup = { name: string | null; items: MenuItem[] };

function groupItemsByTag(items: MenuItem[]): ItemGroup[] {
  const groups: ItemGroup[] = [];
  const indexByKey = new Map<string, number>();
  for (const item of items) {
    const name = itemGroupName(item);
    const key = name ? name.toLowerCase() : '__none__';
    let idx = indexByKey.get(key);
    if (idx === undefined) {
      idx = groups.length;
      indexByKey.set(key, idx);
      groups.push({ name, items: [] });
    }
    groups[idx].items.push(item);
  }
  const named = groups.filter((g) => g.name);
  const rest = groups.filter((g) => !g.name);
  return [...named, ...rest];
}

function categoryHasItemGroups(cat: MenuCategory): boolean {
  return cat.items.some((item) => itemGroupName(item));
}

function sectionHasItemGroups(section: MenuSection): boolean {
  return section.categories.some(categoryHasItemGroups);
}

function groupCategoriesIntoSections(categories: MenuCategory[]): MenuSection[] {
  const sections: MenuSection[] = [];
  const indexByKey = new Map<string, number>();

  for (const cat of categories) {
    const up = categoryGoesUp(cat);
    const tag = groupingTag(cat);
    const name = tagName(tag) || cat.name;
    const key = `${up ? 'up' : 'down'}:${tag ? name.toLowerCase() : cat.id}`;
    const id = `${up ? 'up' : 'down'}-${tag?.id || cat.id}`;

    let idx = indexByKey.get(key);
    if (idx === undefined) {
      idx = sections.length;
      indexByKey.set(key, idx);
      sections.push({ id, name, up, categories: [] });
    }
    sections[idx].categories.push(cat);
  }

  return [...sections.filter((s) => s.up), ...sections.filter((s) => !s.up)];
}

function useIsDesktop() {
  const [desktop, setDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return desktop;
}

export default function DineInMenuScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tableId = searchParams.get('table');
  const caseTable = isHallCaseTable(searchParams);
  const { data: categories = [], isPending, isError, error } = useQuery(dineInMenuQueryOptions());
  const { theme, isLight, tokens, toggleTheme, fadeMs } = useDineInMenuTheme();
  const favoriteIds = useMenuFavoritesStore((s) => s.ids);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [hall, setHall] = useState<HallTable | null>(null);
  const isDesktop = useIsDesktop();
  const didLandOnFood = useRef(false);

  useEffect(() => {
    if (caseTable) {
      setHall(buildCaseHallTable(categories));
      return;
    }
    if (!tableId) {
      setHall(null);
      return;
    }
    let cancelled = false;
    hallApi
      .getTable(tableId)
      .then((row) => {
        if (!cancelled) setHall(row);
      })
      .catch(() => {
        if (!cancelled) setHall(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tableId, caseTable, categories]);

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [navPad, setNavPad] = useState(0);

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const navRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const scrollLockUntil = useRef(0);

  const totalItems = categories.reduce((sum, cat) => sum + cat.items.length, 0);
  const showInitialLoader = isPending && categories.length === 0;
  const showError = isError && categories.length === 0;
  const visibleCategories = useMemo(() => {
    const favoriteSet = new Set(favoriteIds);
    return categories
      .map((cat) => ({
        ...cat,
        items: showFavoritesOnly
          ? cat.items.filter((item) => favoriteSet.has(item.id))
          : cat.items,
      }))
      .filter((c) => c.items.length > 0);
  }, [categories, showFavoritesOnly, favoriteIds]);

  const sections = useMemo(
    () => groupCategoriesIntoSections(visibleCategories),
    [visibleCategories],
  );
  const upSections = useMemo(() => sections.filter((s) => s.up), [sections]);
  const downSections = useMemo(() => sections.filter((s) => !s.up), [sections]);
  const mobileSections = sections;
  const firstFoodId = downSections[0]?.id ?? mobileSections[0]?.id ?? null;

  useEffect(() => {
    if (!firstFoodId || activeSectionId) return;
    setActiveSectionId(firstFoodId);
  }, [firstFoodId, activeSectionId]);

  useEffect(() => {
    const updatePad = () => {
      const el = navRef.current;
      if (!el) return;
      setNavPad(Math.max(0, (el.clientWidth - 112) / 2));
    };
    updatePad();
    window.addEventListener('resize', updatePad);
    return () => window.removeEventListener('resize', updatePad);
  }, [mobileSections.length]);

  const centerTab = useCallback((sectionId: string, smooth = false) => {
    const nav = navRef.current;
    const tab = tabRefs.current[sectionId];
    if (!nav || !tab) return;
    const left = tab.offsetLeft - (nav.clientWidth - tab.offsetWidth) / 2;
    nav.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  const [bottomPad, setBottomPad] = useState(160);
  useEffect(() => {
    const extra = hall ? 64 : 0;
    const update = () => setBottomPad(Math.max(160, Math.round(window.innerHeight * 0.55)) + extra);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [hall]);

  useEffect(() => {
    if (isDesktop || mobileSections.length === 0) return;

    const syncActiveFromScroll = () => {
      if (Date.now() < scrollLockUntil.current) return;

      const doc = document.documentElement;
      const scrollBottom = window.scrollY + window.innerHeight;
      const nearBottom = scrollBottom >= doc.scrollHeight - 48;
      if (nearBottom) {
        const lastId = mobileSections[mobileSections.length - 1].id;
        setActiveSectionId((prev) => (prev === lastId ? prev : lastId));
        return;
      }

      const marker = window.innerHeight * 0.28;
      let currentId = mobileSections[0].id;
      for (const section of mobileSections) {
        const el = sectionRefs.current[section.id];
        if (!el) continue;
        if (el.getBoundingClientRect().top <= marker) {
          currentId = section.id;
        }
      }
      setActiveSectionId((prev) => (prev === currentId ? prev : currentId));
    };

    syncActiveFromScroll();
    window.addEventListener('scroll', syncActiveFromScroll, { passive: true });
    window.addEventListener('resize', syncActiveFromScroll);
    return () => {
      window.removeEventListener('scroll', syncActiveFromScroll);
      window.removeEventListener('resize', syncActiveFromScroll);
    };
  }, [mobileSections, isDesktop]);

  useEffect(() => {
    if (isDesktop || !activeSectionId) return;
    if (Date.now() < scrollLockUntil.current) return;
    centerTab(activeSectionId, false);
  }, [activeSectionId, centerTab, isDesktop]);

  const scrollPageToSection = useCallback((sectionId: string) => {
    const el = sectionRefs.current[sectionId];
    if (!el) return;

    scrollLockUntil.current = Date.now() + 900;
    setActiveSectionId(sectionId);
    centerTab(sectionId, true);

    const top = el.getBoundingClientRect().top + window.scrollY - 12;
    const maxTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    window.scrollTo({ top: Math.min(top, maxTop), behavior: 'smooth' });
  }, [centerTab]);

  const openItem = useCallback(
    (id: string) => navigate(`/menu/product/${id}`),
    [navigate],
  );

  useEffect(() => {
    if (showInitialLoader || didLandOnFood.current) return;
    if (upSections.length === 0 || !firstFoodId) return;
    const hash = window.location.hash.replace('#', '');
    if (hash === 'up') {
      didLandOnFood.current = true;
      return;
    }
    didLandOnFood.current = true;
    scrollLockUntil.current = Date.now() + 800;

    const land = (): boolean => {
      if (isDesktop) {
        const node = document.getElementById('dinein-food');
        if (!node) return false;
        node.scrollIntoView({ block: 'start' });
        return true;
      }
      const el = sectionRefs.current[firstFoodId];
      if (!el) return false;
      const top = el.getBoundingClientRect().top + window.scrollY - 12;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      setActiveSectionId(firstFoodId);
      return true;
    };

    const tryLand = (left: number) => {
      if (land() || left <= 0) return;
      requestAnimationFrame(() => tryLand(left - 1));
    };
    requestAnimationFrame(() => tryLand(8));
  }, [isDesktop, showInitialLoader, upSections.length, firstFoodId]);

  const scrollToSplit = (target: 'up' | 'food') => {
    const node = document.getElementById(target === 'up' ? 'dinein-up' : 'dinein-food');
    node?.scrollIntoView({ behavior: 'smooth', block: target === 'up' ? 'end' : 'start' });
  };

  const headerActions = (
    <div className="flex items-center gap-3">
      {isDesktop ? (
        <>
          <button
            type="button"
            onClick={() => scrollToSplit('up')}
            className="text-[11px] tracking-[0.22em] uppercase active:opacity-70"
            style={{ color: tokens.navInactive, fontFamily: 'Manrope, sans-serif' }}
          >
            Напитки
          </button>
          <button
            type="button"
            onClick={() => scrollToSplit('food')}
            className="text-[11px] tracking-[0.22em] uppercase active:opacity-70"
            style={{ color: tokens.text, fontFamily: 'Manrope, sans-serif' }}
          >
            Кухня
          </button>
        </>
      ) : (
        <span
          className="text-[11px] tracking-[0.28em] uppercase"
          style={{ color: tokens.navInactive, fontFamily: 'Manrope, sans-serif' }}
        >
          Кухня &amp; бар
        </span>
      )}
      <button
        type="button"
        onClick={() => setShowFavoritesOnly((v) => !v)}
        className="w-9 h-9 flex items-center justify-center active:opacity-70"
        aria-pressed={showFavoritesOnly}
        aria-label={showFavoritesOnly ? 'Показать всё меню' : 'Только избранное'}
        style={{
          border: `1px solid ${tokens.border}`,
          color: showFavoritesOnly ? tokens.text : tokens.navInactive,
        }}
      >
        <Bookmark size={16} fill={showFavoritesOnly ? tokens.text : 'none'} />
      </button>
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={isLight ? 'Тёмная тема' : 'Светлая тема'}
        className="w-9 h-9 flex items-center justify-center active:opacity-70"
        style={{
          border: `1px solid ${tokens.border}`,
          color: tokens.text,
        }}
      >
        {isLight ? <Moon size={16} /> : <Sun size={16} />}
      </button>
    </div>
  );

  const statusBlocks = (
    <>
      {showInitialLoader && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader className="animate-spin" size={28} style={{ color: tokens.muted }} />
          <p className="text-sm" style={{ color: tokens.navInactive, fontFamily: 'Manrope, sans-serif' }}>
            Загрузка…
          </p>
        </div>
      )}

      {showError && (
        <div
          className="mb-6 p-4 flex gap-3"
          style={{ border: `1px solid ${tokens.errorBorder}`, background: tokens.errorBg }}
        >
          <AlertCircle size={18} className="flex-shrink-0 mt-0.5" style={{ color: tokens.errorIcon }} />
          <div>
            <p className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Ошибка
            </p>
            <p className="text-xs mt-1" style={{ color: tokens.muted, fontFamily: 'Manrope, sans-serif' }}>
              {error instanceof Error ? error.message : 'Ошибка загрузки меню'}
            </p>
          </div>
        </div>
      )}

      {!showInitialLoader && totalItems === 0 && !showError && (
        <div className="text-center py-16">
          <p style={{ color: tokens.navInactive, fontFamily: 'Manrope, sans-serif' }}>
            Меню пусто
          </p>
        </div>
      )}

      {!showInitialLoader && totalItems > 0 && sections.length === 0 && showFavoritesOnly && (
        <div className="text-center py-16">
          <p style={{ color: tokens.navInactive, fontFamily: 'Manrope, sans-serif' }}>
            Пока нет избранного
          </p>
        </div>
      )}
    </>
  );

  return (
    <DineInThemeShell theme={theme} fadeMs={fadeMs}>
      <div
        className="flex flex-col min-h-screen"
        style={{ color: tokens.text }}
      >
        {isDesktop ? (
          <>
            <header
              className="sticky top-0 z-20"
              style={{
                background: isLight ? 'rgba(246,245,242,0.92)' : 'rgba(10,10,10,0.92)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderBottom: `1px solid ${tokens.borderSoft}`,
              }}
            >
              <div className="flex items-center justify-between px-8 py-4">
                <Link
                  to="/"
                  className="flex items-center gap-0.5 text-[12px] tracking-[0.14em] uppercase"
                  style={{ color: tokens.muted, fontFamily: 'Manrope, sans-serif' }}
                >
                  <ChevronLeft size={16} /> Теория
                </Link>
                {headerActions}
              </div>
            </header>

            <div className="px-8 pt-6">
              {statusBlocks}
            </div>

            {!showInitialLoader && sections.length > 0 && (
              <div className="dinein-pc" style={hall ? { paddingBottom: 120 } : undefined}>
                {upSections.length > 0 && (
                  <div id="dinein-up">
                    {upSections.map((section) => (
                      <DesktopSectionBlock
                        key={section.id}
                        section={section}
                        tokens={tokens}
                        onOpenItem={openItem}
                      />
                    ))}
                  </div>
                )}
                <div id="dinein-food" className="dinein-pc-food">
                  {downSections.map((section) => (
                    <DesktopSectionBlock
                      key={section.id}
                      section={section}
                      tokens={tokens}
                      onOpenItem={openItem}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="max-w-md mx-auto w-full px-4 pt-5" style={{ paddingBottom: bottomPad }}>
              <div className="flex items-center justify-between mb-8">
                <Link
                  to="/"
                  className="flex items-center gap-0.5 text-[12px] tracking-[0.14em] uppercase"
                  style={{ color: tokens.muted, fontFamily: 'Manrope, sans-serif' }}
                >
                  <ChevronLeft size={16} /> Теория
                </Link>
                {headerActions}
              </div>

              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] as const }}
                className="text-[2.6rem] leading-none font-bold uppercase mb-2"
                style={{
                  fontFamily: 'Unbounded, sans-serif',
                  letterSpacing: '-0.04em',
                }}
              >
                Меню
              </motion.h1>
              <div className="flex items-center justify-between gap-3 mb-6">
                <p
                  className="text-sm"
                  style={{ color: tokens.mutedSoft, fontFamily: 'Manrope, sans-serif' }}
                >
                  Цены в зале · Долгопрудный
                </p>
              </div>

              {!showInitialLoader && !showError && (
                <MenuFavoritesOnboarding light={isLight} />
              )}

              {statusBlocks}

              {mobileSections.map((section) => (
                <div
                  key={section.id}
                  ref={(node) => {
                    sectionRefs.current[section.id] = node;
                  }}
                >
                  <MenuSectionBlock
                    section={section}
                    tokens={tokens}
                    onOpenItem={openItem}
                  />
                </div>
              ))}
            </div>

            {(mobileSections.length > 0) && (
              <div
                className="fixed bottom-0 left-0 right-0 z-40"
                style={{
                  background: tokens.navFade,
                  paddingTop: 28,
                }}
              >
                <div className="max-w-md mx-auto px-3 pb-[max(14px,env(safe-area-inset-bottom))]">
                  <div
                    className="relative overflow-hidden"
                    style={{
                      backgroundColor: tokens.navBg,
                      border: `1px solid ${tokens.navBorder}`,
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      boxShadow: isLight ? '0 8px 28px rgba(0,0,0,0.08)' : 'none',
                    }}
                  >
                    {mobileSections.length > 0 && (
                      <div className="relative">
                    <div
                      className="pointer-events-none absolute top-1 bottom-1 left-1/2 -translate-x-1/2"
                      style={{
                        width: 132,
                        backgroundColor: tokens.navHighlight,
                      }}
                    />

                    <div
                      ref={navRef}
                      className="flex items-center gap-1 overflow-x-auto no-scrollbar py-2"
                      style={{
                        WebkitOverflowScrolling: 'touch',
                        scrollPaddingInline: navPad,
                      }}
                    >
                      <div style={{ width: navPad, flexShrink: 0 }} aria-hidden />
                      {mobileSections.map((section) => {
                        const active = section.id === activeSectionId;
                        return (
                          <button
                            key={section.id}
                            type="button"
                            ref={(node) => {
                              tabRefs.current[section.id] = node;
                            }}
                            onClick={() => scrollPageToSection(section.id)}
                            className="shrink-0 px-3 py-2.5 text-[12px] font-semibold tracking-[0.08em] uppercase"
                            style={{
                              color: active ? tokens.navActive : tokens.navInactive,
                              minWidth: 112,
                              textAlign: 'center',
                              fontFamily: 'Manrope, sans-serif',
                            }}
                          >
                            {section.name}
                          </button>
                        );
                      })}
                      <div style={{ width: navPad, flexShrink: 0 }} aria-hidden />
                    </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {hall ? (
        <HallCheckPanel
          variant="dock"
          data={hall}
          tokens={tokens}
          isLight={isLight}
          bottomInset={isDesktop ? 24 : 96}
          payWith={caseTable ? {} : { tableId: hall.tableId, orderId: hall.order?.id }}
          mockOnly={caseTable}
          defaultOpen={caseTable}
          onMockPay={(kind) =>
            navigate(kind === 'tip' ? '/hall-payment-return?case=tipped' : '/hall-payment-return?case=paid')
          }
        />
      ) : null}
    </DineInThemeShell>
  );
}

function splitColumns<T>(items: T[]): [T[], T[]] {
  const mid = Math.ceil(items.length / 2);
  return [items.slice(0, mid), items.slice(mid)];
}

function DesktopSectionBlock({
  section,
  tokens,
  onOpenItem,
}: {
  section: MenuSection;
  tokens: DineInMenuThemeTokens;
  onOpenItem: (id: string) => void;
}) {
  const nested = sectionHasItemGroups(section);
  if (!nested) {
    return (
      <div className="dinein-pc-section">
        {section.categories.map((category) => (
          <DesktopCategoryFlat
            key={category.id}
            category={category}
            tokens={tokens}
            onOpenItem={onOpenItem}
          />
        ))}
      </div>
    );
  }

  return (
    <section className="dinein-pc-section">
      <h2 className="dinein-pc-section-title" style={{ color: tokens.text }}>
        {section.name}
      </h2>
      {section.categories.map((category) => (
        <div key={category.id} className="mb-10">
          <h3 className="dinein-pc-subtitle" style={{ color: tokens.text }}>
            {category.name}
          </h3>
          {groupItemsByTag(category.items).map((group) => (
            <div key={group.name ?? 'rest'}>
              {group.name ? (
                <div className="dinein-pc-group">
                  <span className="dinein-pc-group-title" style={{ color: tokens.text }}>
                    {group.name}
                  </span>
                  <span className="dinein-pc-group-line" style={{ background: tokens.border }} />
                </div>
              ) : null}
              <div className="dinein-pc-grid-3">
                {group.items.map((item) => (
                  <DesktopMenuItem
                    key={item.id}
                    item={item}
                    compact
                    tokens={tokens}
                    onOpen={() => onOpenItem(item.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </section>
  );
}

function DesktopCategoryFlat({
  category,
  tokens,
  onOpenItem,
}: {
  category: MenuCategory;
  tokens: DineInMenuThemeTokens;
  onOpenItem: (id: string) => void;
}) {
  const [left, right] = splitColumns(category.items);
  return (
    <section className="dinein-pc-cat">
      <h2 className="dinein-pc-title" style={{ color: tokens.text }}>
        {category.name}
      </h2>
      <div className="dinein-pc-line" style={{ background: tokens.border }} />
      <div className="dinein-pc-grid">
        <div>
          {left.map((item) => (
            <DesktopMenuItem
              key={item.id}
              item={item}
              tokens={tokens}
              onOpen={() => onOpenItem(item.id)}
            />
          ))}
        </div>
        <div>
          {right.map((item) => (
            <DesktopMenuItem
              key={item.id}
              item={item}
              tokens={tokens}
              onOpen={() => onOpenItem(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function DesktopMenuItem({
  item,
  tokens,
  onOpen,
  compact,
}: {
  item: MenuItem;
  tokens: DineInMenuThemeTokens;
  onOpen: () => void;
  compact?: boolean;
}) {
  const highlighted = isHighlightedItem(item);
  return (
    <div className={`relative ${compact ? 'py-2' : 'py-3'} ${item.isUnavailable ? 'opacity-40' : ''}`}>
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left transition-opacity active:opacity-70 pr-6"
      >
        <div className="flex items-baseline gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-1.5">
              {highlighted ? (
                <ChevronRight
                  size={14}
                  className="shrink-0 relative top-[1px]"
                  style={{ color: tokens.text }}
                  aria-hidden
                />
              ) : null}
              <span
                className="text-[16px] font-semibold leading-snug"
                style={{ color: tokens.text, fontFamily: 'Manrope, sans-serif' }}
              >
                {item.name}
              </span>
            </div>
            {item.description ? (
              <p
                className="text-[13px] mt-1 leading-relaxed"
                style={{ color: tokens.mutedSoft, fontFamily: 'Manrope, sans-serif' }}
              >
                {item.description}
              </p>
            ) : null}
            {item.isUnavailable ? (
              <p
                className="text-[11px] font-semibold tracking-[0.08em] uppercase mt-1"
                style={{ color: tokens.warn, fontFamily: 'Manrope, sans-serif' }}
              >
                Нет в наличии
              </p>
            ) : null}
          </div>
          {item.price > 0 && (
            <span
              className="shrink-0 text-[16px] font-semibold tabular-nums"
              style={{ color: tokens.text, fontFamily: 'Manrope, sans-serif' }}
            >
              {item.price}
            </span>
          )}
        </div>
      </button>
      <div className="absolute top-3.5 right-0 leading-none">
        <MenuFavoriteButton
          itemId={item.id}
          size={13}
          activeColor={tokens.text}
          inactiveColor={tokens.navInactive}
        />
      </div>
    </div>
  );
}

function MenuSectionBlock({
  section,
  tokens,
  onOpenItem,
}: {
  section: MenuSection;
  tokens: DineInMenuThemeTokens;
  onOpenItem: (id: string) => void;
}) {
  const nested = sectionHasItemGroups(section);

  return (
    <section className="mb-14 scroll-mt-3">
      <h2
        className={`font-bold uppercase leading-tight ${nested ? 'text-[1.7rem] mb-5' : 'text-[1.35rem] mb-6'}`}
        style={{
          fontFamily: 'Unbounded, sans-serif',
          letterSpacing: nested ? '0.02em' : '-0.03em',
          color: tokens.text,
        }}
      >
        {section.name}
      </h2>

      {section.categories.map((category) => {
        const groups = groupItemsByTag(category.items);
        const hasGroups = groups.some((g) => g.name);
        return (
          <div key={category.id} className={hasGroups ? 'mb-10' : 'mb-8'}>
            <h3
              className={
                hasGroups
                  ? 'text-[1.15rem] font-bold uppercase tracking-[0.08em] text-center mb-7'
                  : 'text-[11px] font-semibold uppercase tracking-[0.28em] mb-3'
              }
              style={{
                color: hasGroups ? tokens.text : tokens.navInactive,
                fontFamily: hasGroups ? 'Unbounded, sans-serif' : 'Manrope, sans-serif',
              }}
            >
              {category.name}
            </h3>
            {hasGroups ? (
              groups.map((group) => (
                <div key={group.name ?? 'rest'} className="mb-7 last:mb-0">
                  {group.name ? (
                    <div className="flex items-center gap-3 mb-2.5">
                      <span
                        className="shrink-0 text-[13px] font-bold uppercase tracking-[0.16em]"
                        style={{
                          color: tokens.text,
                          fontFamily: 'Unbounded, sans-serif',
                        }}
                      >
                        {group.name}
                      </span>
                      <span className="h-px flex-1" style={{ background: tokens.border }} />
                    </div>
                  ) : (
                    <div className="h-px mb-2.5" style={{ background: tokens.border }} />
                  )}
                  {group.items.map((item, index) => (
                    <DineInMenuItemRow
                      key={item.id}
                      item={item}
                      isLast={index === group.items.length - 1}
                      highlighted={isHighlightedItem(item)}
                      tokens={tokens}
                      dense
                      onOpen={() => onOpenItem(item.id)}
                    />
                  ))}
                </div>
              ))
            ) : (
              <div style={{ borderTop: `1px solid ${tokens.border}` }}>
                {category.items.map((item, index) => (
                  <DineInMenuItemRow
                    key={item.id}
                    item={item}
                    isLast={index === category.items.length - 1}
                    highlighted={isHighlightedItem(item)}
                    tokens={tokens}
                    onOpen={() => onOpenItem(item.id)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

function DineInMenuItemRow({
  item,
  isLast,
  highlighted,
  tokens,
  onOpen,
  dense,
}: {
  item: MenuItem;
  isLast: boolean;
  highlighted?: boolean;
  tokens: DineInMenuThemeTokens;
  onOpen: () => void;
  dense?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`relative ${dense ? 'py-2.5' : 'py-3.5'} ${item.isUnavailable ? 'opacity-40' : ''}`}
      style={{
        borderBottom: isLast || dense ? 'none' : `1px solid ${tokens.borderSoft}`,
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left transition-opacity active:opacity-70"
      >
        <div className="flex items-baseline gap-2">
          {highlighted ? (
            <ChevronRight
              size={14}
              className="shrink-0 relative top-[1px]"
              style={{ color: tokens.text }}
              aria-hidden
            />
          ) : null}
          <h3
            className="min-w-0 flex-1 text-[15px] font-semibold leading-snug pr-1"
            style={{
              color: tokens.text,
              letterSpacing: '-0.01em',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            {item.name}
          </h3>
          <span
            className="shrink-0 text-[13px] tracking-[0.04em]"
            style={{ color: tokens.faint, fontFamily: 'Manrope, sans-serif' }}
            aria-hidden
          >
            ···
          </span>
          {item.price > 0 && (
            <span
              className="shrink-0 text-[15px] font-bold tabular-nums"
              style={{ color: tokens.text, fontFamily: 'Manrope, sans-serif' }}
            >
              {item.price} ₽
            </span>
          )}
          <span className="shrink-0 w-4" aria-hidden />
        </div>
        {item.description && (
          <p
            className={`text-[12px] mt-1.5 line-clamp-2 leading-relaxed pr-6 ${highlighted ? 'pl-5' : ''}`}
            style={{ color: tokens.mutedSoft, fontFamily: 'Manrope, sans-serif' }}
          >
            {item.description}
          </p>
        )}
        {item.isUnavailable && (
          <p
            className={`text-[11px] font-semibold tracking-[0.08em] uppercase mt-1.5 ${highlighted ? 'pl-5' : ''}`}
            style={{ color: tokens.warn, fontFamily: 'Manrope, sans-serif' }}
          >
            Нет в наличии
          </p>
        )}
      </button>
      <div className="absolute top-3.5 right-0 leading-none">
        <MenuFavoriteButton
          itemId={item.id}
          size={14}
          activeColor={tokens.text}
          inactiveColor={tokens.navInactive}
        />
      </div>
    </motion.div>
  );
}
